import { DT } from '../../theme/designTokens';
import type { LobbyState } from '../../types/game';
import { tableSkinAsset, tileSkinThumbnail, type EffectiveCosmetics } from '../../config/viewerCosmetics';
import { PlayerAvatar } from '../../components/ui/PlayerAvatar';
import { CountryFlag } from '../../components/ui/CountryFlag';
import { showsReadyGlow } from './lobbyLogic';
import { useTranslation } from '../../i18n/react';
import { getPlayerName } from '../../utils/playerName';

// Seat → placement + orientation around the square table (Figma composition:
// teams face each other — positions 2/0 top-bottom (portrait cards), 1/3
// left-right (landscape cards). Team A = red (0,2), Team B = blue (1,3).
type Orientation = 'portrait' | 'landscape';
const SEAT: Record<number, { style: React.CSSProperties; orient: Orientation }> = {
  2: { orient: 'portrait',  style: { top: '-9%', left: '50%', transform: 'translateX(-50%)' } },   // top
  0: { orient: 'portrait',  style: { bottom: '-9%', left: '50%', transform: 'translateX(-50%)' } },// bottom (host/you)
  1: { orient: 'landscape', style: { left: '-10%', top: '50%', transform: 'translateY(-50%)' } },  // left
  3: { orient: 'landscape', style: { right: '-10%', top: '50%', transform: 'translateY(-50%)' } }, // right
};
const TEAM_COLOR: Record<number, string> = { 0: '#880000', 1: '#2371C0' };

/** One rectangular seat card around the table (Figma seat card, NOT a circle).
 *  When empty and `onSelectSeat` is provided, the card is a clickable button
 *  that seats the viewer there — same interaction as the Play Online table. */
function SeatCard({ position, lobby, scale, onSelectSeat }: {
  position: number; lobby: LobbyState; scale: number; onSelectSeat?: (position: number) => void;
}) {
  const { t } = useTranslation('play');
  const player = lobby.players.find((p) => p.position === position);
  const team = position % 2;
  const color = TEAM_COLOR[team];
  const ready = showsReadyGlow(player);
  const { style, orient } = SEAT[position];
  const w = Math.round((orient === 'portrait' ? 58 : 84) * scale);
  const h = Math.round((orient === 'portrait' ? 84 : 58) * scale);
  const occupied = !!player;
  const isBot = !!player?.is_bot;
  const claimable = !occupied && !!onSelectSeat;
  // Shared structured label seam (Phase 9): bots by stable difficulty,
  // humans verbatim — kept in lockstep with the seat rows beside the preview.
  const seatName = player ? getPlayerName(player, player.display_name) : '';

  const common: React.CSSProperties = {
    width: w, height: h, borderRadius: 14, boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
    padding: 4,
    background: occupied ? color : (claimable ? TEAM_COLOR[team] : 'rgba(11,11,11,0.72)'),
    // Empty seats: gold DASHED outline + gold "+". Occupied: solid team card.
    border: occupied ? `1px solid ${color}` : `1px dashed ${DT.color.gold}`,
    boxShadow: ready ? `0 0 0 2px ${DT.color.success}, 0 0 14px 3px ${DT.color.success}` : 'none',
    transition: `box-shadow ${DT.motion.slow}ms ease, transform .1s ease`,
  };

  const avatarSize = Math.round(30 * scale);
  const inner = occupied ? (
    <>
      {/* Authoritative avatar (Player Identity V1): human preset or the bot's
          stable per-session assignment; no key → display-name initial (the
          initial is the AVATAR fallback only — the name below stays full).
          Flag badge on the avatar's corner (humans only) — same corner-badge
          treatment as the Play Online seat chips. */}
      <span style={{ position: 'relative', width: avatarSize, height: avatarSize, flexShrink: 0 }}>
        <PlayerAvatar
          avatarKey={player!.avatar_key}
          name={seatName}
          size={avatarSize}
          loading="eager"
          style={{ border: '1px solid #FFFFFF' }}
          initialStyle={{ background: '#F5F5F5', color: '#0b0b0b', fontSize: 15 * scale }}
        />
        {!isBot && (
          <CountryFlag
            countryCode={player!.country_code}
            width={Math.max(12, Math.round(avatarSize * 0.6))}
            loading="eager"
            decorative
            style={{ position: 'absolute', right: -4, bottom: -3, boxShadow: '0 0 0 1px rgba(0,0,0,0.4)' }}
          />
        )}
      </span>
      {/* Full resolved display name — controlled ellipsis across the card
          width, complete name preserved in the title attribute. Never a
          single initial. */}
      <span title={seatName} style={{ fontFamily: DT.font.sans, fontWeight: 700, fontSize: Math.round(9 * scale), lineHeight: 1, color: '#FFFFFF', maxWidth: w - 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {seatName}
      </span>
    </>
  ) : (
    <span data-empty-plus="true" style={{ fontFamily: DT.font.sans, fontWeight: 500, fontSize: Math.round(28 * scale), lineHeight: 1, color: DT.color.gold }}>+</span>
  );

  const dataProps = {
    'data-seat': position,
    'data-team': team === 0 ? 'A' : 'B',
    'data-occupied': occupied ? 'true' : 'false',
    'data-bot': isBot ? 'true' : 'false',
    'data-ready': ready ? 'true' : 'false',
  };

  return (
    <div style={{ position: 'absolute', ...style }}>
      {claimable ? (
        <button
          type="button"
          {...dataProps}
          aria-label={t('lobby.sitInSeat', { position })}
          onClick={() => onSelectSeat!(position)}
          className="lobby-preview-seat"
          style={{ ...common, cursor: 'pointer' }}
        >
          {inner}
        </button>
      ) : (
        <div
          {...dataProps}
          aria-label={player
            ? (ready ? t('lobby.seatAriaReady', { name: seatName }) : t('lobby.seatAria', { name: seatName }))
            : t('lobby.emptySeatAria', { position })}
          style={common}
        >
          {inner}
        </div>
      )}
    </div>
  );
}

/**
 * Live, viewer-specific cosmetic preview: the selected real table asset with a
 * representative real domino from the selected tile skin, plus the four
 * rectangular team seat cards (Figma composition) driven by real lobby seat
 * state. Empty seats are clickable (via `onSelectSeat`) to sit the viewer there,
 * exactly like the Play Online table. Never uses the Figma-embedded artwork.
 */
export function LobbyTablePreview({ lobby, effective, size = 380, onSelectSeat }: {
  lobby: LobbyState; effective: EffectiveCosmetics; size?: number; onSelectSeat?: (position: number) => void;
}) {
  const scale = size / 300; // seat metrics were tuned at 300; scale with the table
  const { t } = useTranslation('play');
  return (
    <div
      data-testid="lobby-table-preview"
      style={{ position: 'relative', width: '100%', maxWidth: size, aspectRatio: '1 / 1', margin: '0 auto' }}
    >
      <style>{`.lobby-preview-seat:hover, .lobby-preview-seat:focus-visible { transform: scale(1.06); }`}</style>
      {/* Real table asset (never the Figma raster) */}
      <img
        src={tableSkinAsset(effective.tableSkin)}
        alt={t('lobby.tableAlt', { skin: effective.tableSkin })}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
      {/* Representative real domino from the selected tile skin */}
      <img
        src={tileSkinThumbnail(effective.tileSkin)}
        alt={t('lobby.dominoAlt', { skin: effective.tileSkin })}
        style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%) rotate(-90deg)',
          width: '28%', height: 'auto',
          filter: 'drop-shadow(0 8px 10px rgba(0,0,0,0.4))',
        }}
      />
      {[0, 1, 2, 3].map((pos) => (
        <SeatCard key={pos} position={pos} lobby={lobby} scale={scale} onSelectSeat={onSelectSeat} />
      ))}
    </div>
  );
}
